import { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'sonner';
import { CalendarDays, ChevronLeft, ChevronRight, Columns3, LayoutList, Loader2, Plus, Sparkles, Trash2, Upload, Film, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NativeSelect } from '@/components/ui/native-select';
import api from '@/lib/api';

/* Contentplan (v8 M22, 22.10.2026): Redaktionsplan für P1, PTL und den persönlichen Account. Monat, Woche, Board; Drag & Drop
   ändert Datum bzw. Status (PATCH mit Version, 409 = „inzwischen geändert“). Die Seite läuft ohne KI; die KI-Knöpfe erscheinen
   nur, wenn KI_CONTENT an ist. Es wird nichts automatisch gepostet. */

const FARBEN = {
  rot: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-950/60 dark:text-red-100 dark:border-red-800',
  orange: 'bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-950/60 dark:text-orange-100 dark:border-orange-800',
  gelb: 'bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-950/60 dark:text-yellow-100 dark:border-yellow-800',
  gruen: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-100 dark:border-emerald-800',
  tuerkis: 'bg-teal-100 text-teal-900 border-teal-300 dark:bg-teal-950/60 dark:text-teal-100 dark:border-teal-800',
  blau: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950/60 dark:text-blue-100 dark:border-blue-800',
  violett: 'bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-950/60 dark:text-violet-100 dark:border-violet-800',
  rosa: 'bg-pink-100 text-pink-900 border-pink-300 dark:bg-pink-950/60 dark:text-pink-100 dark:border-pink-800',
  grau: 'bg-muted text-foreground border-border',
};
const PUNKT = { rot: 'bg-red-500', orange: 'bg-orange-500', gelb: 'bg-yellow-500', gruen: 'bg-emerald-500', tuerkis: 'bg-teal-500', blau: 'bg-blue-500', violett: 'bg-violet-500', rosa: 'bg-pink-500', grau: 'bg-muted-foreground' };
const STATUS_LABEL = { idee: 'Idee', entwurf: 'Entwurf', freigabe: 'Zur Freigabe', geplant: 'Geplant', gepostet: 'Gepostet' };
const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const fehler = (e, std) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : std);
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const aus = (s) => { const [y, m, t] = s.split('-').map(Number); return new Date(y, m - 1, t); };
const plus = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const montagVon = (d) => plus(d, -((d.getDay() + 6) % 7));
const kurz = (d) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });

function useBreit() {
  const [breit, setBreit] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);
  useEffect(() => {
    const m = window.matchMedia('(min-width: 768px)');
    const f = () => setBreit(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  return breit;
}

function Chip({ b, saeule, onClick, index, ziehbar }) {
  const klasse = FARBEN[saeule?.farbe || 'grau'];
  const inhalt = (prov, snap) => (
    <button
      type="button" ref={prov?.innerRef} {...(prov?.draggableProps || {})} {...(prov?.dragHandleProps || {})}
      onClick={() => onClick(b)}
      className={`w-full min-w-0 text-left rounded-md border px-2 py-1 text-xs leading-tight ${klasse} ${snap?.isDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}
      style={prov?.draggableProps?.style}
    >
      <span className="block truncate font-medium">{b.titel}</span>
      <span className="block truncate opacity-75">{b.kanal.replace('_', ' ')}{b.uhrzeit ? ` · ${b.uhrzeit}` : ''}{b.status !== 'idee' ? ` · ${STATUS_LABEL[b.status]}` : ' · Idee'}</span>
    </button>
  );
  if (!ziehbar) return inhalt();
  return <Draggable draggableId={b.id} index={index}>{(p, s) => inhalt(p, s)}</Draggable>;
}

function Spalte({ id, kopf, beitraege, saeulen, onOpen, klasse = '', min = '' }) {
  return (
    <Droppable droppableId={id}>
      {(prov, snap) => (
        <div ref={prov.innerRef} {...prov.droppableProps}
          className={`rounded-lg border p-1.5 space-y-1.5 ${snap.isDraggingOver ? 'bg-primary/5 border-primary/40' : 'bg-card'} ${klasse} ${min}`}>
          {kopf}
          {beitraege.map((b, i) => <Chip key={b.id} b={b} index={i} saeule={saeulen[b.saeule_id]} onClick={onOpen} ziehbar />)}
          {prov.placeholder}
        </div>
      )}
    </Droppable>
  );
}

function MedienVorschau({ m, onWeg, darfWeg }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alt = null; let aktiv = true;
    api.get(`/content/medien/${m.id}`, { responseType: 'blob' }).then(r => { if (aktiv) { alt = URL.createObjectURL(r.data); setUrl(alt); } }).catch(() => {});
    return () => { aktiv = false; if (alt) URL.revokeObjectURL(alt); };
  }, [m.id]);
  return (
    <div className="relative w-24 h-24 rounded-md border overflow-hidden bg-muted shrink-0">
      {url && (m.typ === 'video'
        ? <video src={url} className="w-full h-full object-cover" muted controls playsInline />
        : <img src={url} alt={m.name} className="w-full h-full object-cover" />)}
      {!url && <Film className="w-5 h-5 m-auto absolute inset-0 text-muted-foreground" />}
      {darfWeg && <button type="button" onClick={() => onWeg(m)} aria-label="Datei entfernen"
        className="absolute top-0.5 right-0.5 rounded bg-background/90 p-1 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
    </div>
  );
}

function BeitragPanel({ b, offen, onClose, st, saeulen, hashtagsBlock, kiAn, onGespeichert, onReload }) {
  const [d, setD] = useState(b);
  const [busy, setBusy] = useState('');
  const [vorschlag, setVorschlag] = useState(null);
  useEffect(() => { setD(b); setVorschlag(null); }, [b]);
  if (!b) return null;
  const leitung = st.rolle === 'leitung';
  const gesperrt = !leitung && st.nur_leitung_status.includes(b.status);
  const set = (k, v) => setD(x => ({ ...x, [k]: v }));
  const statusOptionen = st.status.filter(s => leitung || !st.nur_leitung_status.includes(s)).map(s => ({ value: s, label: STATUS_LABEL[s] }));

  const speichern = async () => {
    setBusy('speichern');
    try {
      const { titel, saeule_id, kanal, status, datum, uhrzeit, hook, caption, hashtags, notiz } = d;
      const r = await api.patch(`/content/beitraege/${b.id}`, { version: b.version, titel, saeule_id, kanal, status, datum: datum || null, uhrzeit: uhrzeit || '', hook, caption, hashtags, notiz });
      onGespeichert(r.data); toast.success('Gespeichert');
    } catch (e) {
      toast.error(fehler(e, 'Speichern nicht möglich'));
      if (e?.response?.status === 409) onReload();
    } finally { setBusy(''); }
  };
  const loeschen = async () => {
    if (!window.confirm('Diesen Beitrag löschen?')) return;
    try { await api.delete(`/content/beitraege/${b.id}`); onGespeichert(null, b.id); toast.success('Gelöscht'); } catch (e) { toast.error(fehler(e, 'Löschen nicht möglich')); }
  };
  const hochladen = async (ev) => {
    const f = ev.target.files?.[0]; ev.target.value = '';
    if (!f) return;
    setBusy('upload');
    try {
      const fd = new FormData(); fd.append('datei', f);
      const r = await api.post(`/content/beitraege/${b.id}/medien`, fd);
      onGespeichert({ ...b, medien: [...(b.medien || []), r.data] });
    } catch (e) { toast.error(fehler(e, 'Hochladen nicht möglich')); } finally { setBusy(''); }
  };
  const medienWeg = async (m) => {
    try { await api.delete(`/content/medien/${m.id}`); onGespeichert({ ...b, medien: (b.medien || []).filter(x => x.id !== m.id) }); } catch (e) { toast.error(fehler(e, 'Entfernen nicht möglich')); }
  };
  const ki = async () => {
    setBusy('ki');
    try { setVorschlag((await api.post('/content/ki/texte', { beitrag_id: b.id })).data); } catch (e) { toast.error(fehler(e, 'KI nicht erreichbar')); } finally { setBusy(''); }
  };

  return (
    <Sheet open={offen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-4 sm:p-6">
        <SheetHeader><SheetTitle>Beitrag</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-4 pb-6">
          {gesperrt && <p className="text-xs rounded bg-muted p-2">Geplante und gepostete Beiträge ändert nur die Leitung.</p>}
          {d.notiz && b.quelle === 'ereignis' && /Keine Fotos|Minderjährig/.test(d.notiz) && (
            <p className="text-xs rounded border border-amber-400 bg-amber-50 dark:bg-amber-950/40 p-2">{d.notiz}</p>
          )}
          <label className="block text-xs font-medium">Titel<Input value={d.titel || ''} onChange={e => set('titel', e.target.value)} disabled={gesperrt} className="mt-1" /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-xs font-medium">Säule
              <NativeSelect className="mt-1" value={d.saeule_id || ''} onChange={v => set('saeule_id', v || null)} disabled={gesperrt}
                options={[{ value: '', label: '– ohne –' }, ...Object.values(saeulen).filter(s => s.marke === b.marke && (s.aktiv || s.id === b.saeule_id)).map(s => ({ value: s.id, label: s.name }))]} /></label>
            <label className="block text-xs font-medium">Kanal
              <NativeSelect className="mt-1" value={d.kanal} onChange={v => set('kanal', v)} disabled={gesperrt} options={st.kanaele.map(k => ({ value: k.key, label: k.label }))} /></label>
            <label className="block text-xs font-medium">Status
              <NativeSelect className="mt-1" value={d.status} onChange={v => set('status', v)} options={statusOptionen} /></label>
            <label className="block text-xs font-medium">Datum<Input type="date" value={d.datum || ''} onChange={e => set('datum', e.target.value)} disabled={gesperrt} className="mt-1" /></label>
            <label className="block text-xs font-medium">Uhrzeit<Input type="time" value={d.uhrzeit || ''} onChange={e => set('uhrzeit', e.target.value)} disabled={gesperrt} className="mt-1" /></label>
          </div>
          <label className="block text-xs font-medium">Hook<Input value={d.hook || ''} onChange={e => set('hook', e.target.value)} disabled={gesperrt} className="mt-1" /></label>
          <label className="block text-xs font-medium">Caption<Textarea rows={6} value={d.caption || ''} onChange={e => set('caption', e.target.value)} disabled={gesperrt} className="mt-1" /></label>
          <label className="block text-xs font-medium">Hashtags<Textarea rows={2} value={d.hashtags || ''} onChange={e => set('hashtags', e.target.value)} disabled={gesperrt} className="mt-1" />
            {hashtagsBlock && !gesperrt && <button type="button" className="text-xs text-primary mt-1" onClick={() => set('hashtags', hashtagsBlock)}>Fester Block der Marke einsetzen</button>}</label>
          <label className="block text-xs font-medium">Notiz<Textarea rows={2} value={d.notiz || ''} onChange={e => set('notiz', e.target.value)} disabled={gesperrt} className="mt-1" /></label>

          {kiAn && !gesperrt && (
            <div className="rounded-lg border p-3 space-y-2">
              <Button size="sm" variant="outline" onClick={ki} disabled={busy === 'ki'}>
                {busy === 'ki' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}Texte vorschlagen</Button>
              {vorschlag && (
                <div className="space-y-2 text-sm">
                  <p className="text-xs text-muted-foreground">Vorschlag der KI — erst „übernehmen“ und Speichern macht daraus etwas.</p>
                  {vorschlag.hooks.map((h, i) => (
                    <button key={i} type="button" onClick={() => set('hook', h)} className="block w-full text-left rounded border p-2 hover:bg-accent">{h}</button>
                  ))}
                  <p className="whitespace-pre-wrap rounded border p-2">{vorschlag.caption}</p>
                  <Button size="sm" onClick={() => { set('caption', vorschlag.caption); if (vorschlag.hashtags) set('hashtags', vorschlag.hashtags); }}>Caption übernehmen</Button>
                </div>
              )}
            </div>
          )}

          <div>
            <p className="text-xs font-medium mb-1">Medien</p>
            <div className="flex gap-2 flex-wrap">
              {(b.medien || []).map(m => <MedienVorschau key={m.id} m={m} onWeg={medienWeg} darfWeg={!gesperrt} />)}
              {!gesperrt && (
                <label className="w-24 h-24 rounded-md border border-dashed flex flex-col items-center justify-center text-xs text-muted-foreground cursor-pointer hover:bg-accent">
                  {busy === 'upload' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mb-1" />}Hochladen
                  <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime" onChange={hochladen} />
                </label>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-2 sticky bottom-0 bg-background py-2">
            <Button onClick={speichern} disabled={busy === 'speichern' || gesperrt}>{busy === 'speichern' && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button>
            {!gesperrt && <Button variant="ghost" onClick={loeschen} className="text-destructive ml-auto"><Trash2 className="w-4 h-4 mr-1" />Löschen</Button>}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function ContentPlanPage() {
  const breit = useBreit();
  const [st, setSt] = useState(null);
  const [marke, setMarke] = useState(() => { try { return localStorage.getItem('p1_content_marke') || 'p1'; } catch { return 'p1'; } });
  const [ansicht, setAnsicht] = useState(() => { try { return localStorage.getItem('p1_content_ansicht') || 'monat'; } catch { return 'monat'; } });
  const [anker, setAnker] = useState(() => new Date());
  const [liste, setListe] = useState([]);
  const [saeulenListe, setSaeulenListe] = useState([]);
  const [hashtags, setHashtags] = useState({});
  const [offen, setOffen] = useState(null);
  const [lade, setLade] = useState(true);
  const [planDialog, setPlanDialog] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const [anzahl, setAnzahl] = useState(5);

  const wahl = (setter, key) => (v) => { setter(v); try { localStorage.setItem(key, v); } catch { /* ohne Speicher weiter */ } };
  const saeulen = useMemo(() => Object.fromEntries(saeulenListe.map(s => [s.id, s])), [saeulenListe]);

  const laden = useCallback(async () => {
    try {
      const [s, b, sa, h] = await Promise.all([api.get('/content/status'), api.get('/content/beitraege', { params: { marke } }),
        api.get('/content/saeulen'), api.get('/content/hashtags')]);
      setSt(s.data); setListe(b.data.beitraege); setSaeulenListe(sa.data.saeulen); setHashtags(h.data.hashtags);
    } catch (e) { toast.error(fehler(e, 'Contentplan nicht erreichbar')); } finally { setLade(false); }
  }, [marke]);
  useEffect(() => { laden(); }, [laden]);

  const ersetzen = (neu, geloeschtId) => {
    setListe(l => geloeschtId ? l.filter(x => x.id !== geloeschtId) : l.map(x => x.id === neu.id ? neu : x));
    if (geloeschtId) setOffen(null); else setOffen(neu);
  };

  const verschieben = async (res) => {
    const { draggableId, destination, source } = res;
    if (!destination || destination.droppableId === source.droppableId) return;
    const b = liste.find(x => x.id === draggableId); if (!b) return;
    const ziel = destination.droppableId;
    const feld = ziel.startsWith('s:') ? { status: ziel.slice(2) } : { datum: ziel === 'd:none' ? null : ziel.slice(2) };
    setListe(l => l.map(x => x.id === b.id ? { ...x, ...feld } : x));
    try {
      const r = await api.post(`/content/beitraege/${b.id}/move`, { version: b.version, ...feld });
      setListe(l => l.map(x => x.id === b.id ? r.data : x));
    } catch (e) {
      toast.error(e?.response?.status === 409 && !String(e.response.data?.detail).includes('Leitung') ? 'Wurde inzwischen geändert — Plan neu geladen.' : fehler(e, 'Verschieben nicht möglich'));
      laden();
    }
  };

  const neu = async (datum) => {
    try {
      const r = await api.post('/content/beitraege', { marke, titel: 'Neue Idee', datum: datum || null });
      setListe(l => [...l, r.data]); setOffen(r.data);
    } catch (e) { toast.error(fehler(e, 'Anlegen nicht möglich')); }
  };

  const wochenplan = async () => {
    setPlanBusy(true);
    try {
      const r = await api.post('/content/ki/wochenplan', { marke, woche: iso(montagVon(anker)), anzahl: Number(anzahl) });
      toast.success(`${r.data.beitraege.length} Ideen angelegt`); setPlanDialog(false); laden();
    } catch (e) { toast.error(fehler(e, 'KI nicht erreichbar')); } finally { setPlanBusy(false); }
  };

  if (lade) return <div className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!st) return <div className="p-6 text-sm text-muted-foreground">Der Contentplan ist gerade nicht erreichbar.</div>;

  const nachTag = (tag) => liste.filter(b => b.datum === tag);
  const heute = iso(new Date());
  const schieb = (n) => setAnker(a => ansicht === 'monat' ? new Date(a.getFullYear(), a.getMonth() + n, 1) : plus(a, 7 * n));
  const wochenStart = montagVon(anker);
  const monatTitel = anker.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  const titel = ansicht === 'monat' ? monatTitel : `${kurz(wochenStart)} – ${kurz(plus(wochenStart, 6))}.${wochenStart.getFullYear()}`;
  const tagKopf = (d) => <div className={`text-[11px] font-medium px-1 ${iso(d) === heute ? 'text-primary' : 'text-muted-foreground'}`}>{WOCHENTAGE[(d.getDay() + 6) % 7]} {d.getDate()}.</div>;

  let monatsTage = [];
  if (ansicht === 'monat') {
    const erster = new Date(anker.getFullYear(), anker.getMonth(), 1);
    const start = montagVon(erster);
    const wochen = Math.ceil(((erster.getDay() + 6) % 7 + new Date(anker.getFullYear(), anker.getMonth() + 1, 0).getDate()) / 7);
    monatsTage = Array.from({ length: wochen * 7 }, (_, i) => plus(start, i));
  }

  const ansichten = [['monat', 'Monat', CalendarDays], ['woche', 'Woche', LayoutList], ['board', 'Board', Columns3]];

  return (
    <div className="p-3 sm:p-6 space-y-4 min-w-0 max-w-full" data-testid="content-plan">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl sm:text-2xl font-bold mr-auto">Contentplan</h1>
        <NativeSelect className="w-auto" value={marke} onChange={wahl(setMarke, 'p1_content_marke')} options={st.marken.map(m => ({ value: m.key, label: m.label }))} />
        <div className="flex rounded-md border overflow-hidden">
          {ansichten.map(([k, l, Icon]) => (
            <button key={k} type="button" onClick={() => wahl(setAnsicht, 'p1_content_ansicht')(k)}
              className={`px-3 py-1.5 text-sm flex items-center gap-1 ${ansicht === k ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}><Icon className="w-4 h-4" />{l}</button>
          ))}
        </div>
        <Button size="sm" onClick={() => neu(null)}><Plus className="w-4 h-4 mr-1" />Neu</Button>
        {st.ki.verfuegbar && <Button size="sm" variant="outline" onClick={() => setPlanDialog(true)}><Sparkles className="w-4 h-4 mr-1" />Wochenplan (KI)</Button>}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {ansicht !== 'board' && (
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" onClick={() => schieb(-1)} aria-label="Zurück"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium min-w-[9rem] text-center capitalize">{titel}</span>
            <Button size="icon" variant="ghost" onClick={() => schieb(1)} aria-label="Weiter"><ChevronRight className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => setAnker(new Date())}>Heute</Button>
          </div>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {saeulenListe.filter(s => s.marke === marke && s.aktiv).map(s => <span key={s.id} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${PUNKT[s.farbe]}`} />{s.name}</span>)}
        </div>
      </div>

      <DragDropContext onDragEnd={verschieben}>
        {ansicht === 'monat' && (breit ? (
          <div className="grid grid-cols-7 gap-1">
            {WOCHENTAGE.map(t => <div key={t} className="text-xs text-center text-muted-foreground">{t}</div>)}
            {monatsTage.map(d => (
              <Spalte key={iso(d)} id={`d:${iso(d)}`} beitraege={nachTag(iso(d))} saeulen={saeulen} onOpen={setOffen} min="min-h-[6.5rem]"
                klasse={d.getMonth() !== anker.getMonth() ? 'opacity-50' : ''}
                kopf={<div className="flex items-center justify-between"><span className={`text-[11px] px-1 ${iso(d) === heute ? 'font-bold text-primary' : 'text-muted-foreground'}`}>{d.getDate()}.</span>
                  <button type="button" className="text-muted-foreground hover:text-primary" onClick={() => neu(iso(d))} aria-label="Beitrag anlegen"><Plus className="w-3 h-3" /></button></div>} />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {monatsTage.filter(d => d.getMonth() === anker.getMonth() && nachTag(iso(d)).length).map(d => (
              <div key={iso(d)} className="rounded-lg border p-2 space-y-1.5">
                {tagKopf(d)}
                {nachTag(iso(d)).map(b => <Chip key={b.id} b={b} saeule={saeulen[b.saeule_id]} onClick={setOffen} />)}
              </div>
            ))}
            {!monatsTage.some(d => d.getMonth() === anker.getMonth() && nachTag(iso(d)).length) && <p className="text-sm text-muted-foreground">In diesem Monat ist nichts geplant.</p>}
          </div>
        ))}

        {ansicht === 'woche' && (
          <>
            <div className="overflow-x-auto pb-2">
              <div className="grid grid-cols-7 gap-1.5 min-w-[56rem]">
                {Array.from({ length: 7 }, (_, i) => plus(wochenStart, i)).map(d => (
                  <Spalte key={iso(d)} id={`d:${iso(d)}`} beitraege={nachTag(iso(d))} saeulen={saeulen} onOpen={setOffen} min="min-h-[10rem]" kopf={tagKopf(d)} />
                ))}
              </div>
            </div>
            <Spalte id="d:none" beitraege={liste.filter(b => !b.datum)} saeulen={saeulen} onOpen={setOffen} min="min-h-[3rem]"
              kopf={<div className="text-xs font-medium text-muted-foreground px-1">Ohne Datum (hierher ziehen = Datum entfernen)</div>} />
          </>
        )}

        {ansicht === 'board' && (
          <div className="overflow-x-auto pb-2">
            <div className="flex gap-2 min-w-max">
              {st.status.map(s => (
                <div key={s} className="w-64 shrink-0">
                  <Spalte id={`s:${s}`} beitraege={liste.filter(b => b.status === s)} saeulen={saeulen} onOpen={setOffen} min="min-h-[10rem]"
                    kopf={<div className="text-xs font-semibold px-1 flex justify-between"><span>{STATUS_LABEL[s]}</span><span className="text-muted-foreground">{liste.filter(b => b.status === s).length}</span></div>} />
                </div>
              ))}
            </div>
          </div>
        )}
      </DragDropContext>

      {offen && (
        <BeitragPanel b={offen} offen onClose={() => setOffen(null)} st={st} saeulen={saeulen} hashtagsBlock={hashtags[offen.marke]} kiAn={st.ki.verfuegbar}
          onGespeichert={ersetzen} onReload={laden} />
      )}

      <Dialog open={planDialog} onOpenChange={setPlanDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Wochenplan mit KI</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Legt Ideen für die Woche {kurz(wochenStart)}. an ({st.marken.find(m => m.key === marke)?.label}). Die KI sieht nur Marke und Säulennamen.</p>
          <label className="text-xs font-medium">Anzahl Beiträge
            <NativeSelect className="mt-1" value={String(anzahl)} onChange={setAnzahl} options={[3, 4, 5, 6, 7].map(n => ({ value: String(n), label: String(n) }))} /></label>
          <Button onClick={wochenplan} disabled={planBusy}>{planBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Ideen anlegen</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
